<script setup lang="ts">
import { Dropdown, type Placement, TriggerEvent } from 'floating-vue';
import { onMounted, onUnmounted, ref, watch } from 'vue';

interface Props {
  triggers?: TriggerEvent[];
  showTriggers?: TriggerEvent[];
  hideTriggers?: TriggerEvent[];
  placement?: Placement;
  distance?: number;
  maxWidth?: string;
  isAlwaysOpen?: boolean;
  isPersistent?: boolean;
  shouldStayOpen?: boolean;
  destroyOnClose?: boolean;
  onUpdate?: (open: boolean) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  triggers: () => ['click'],
  showTriggers: undefined,
  hideTriggers: undefined,
  placement: 'bottom-end',
  distance: 4,
  maxWidth: undefined,
  onUpdate: undefined,
  onOpen: undefined,
  onClose: undefined,
});

const dropdownBody = ref<HTMLElement>();
const isOpen = ref(false);

const handleUpdateVisibility = (value: boolean) => {
  isOpen.value = value;
};

const handleClickMenu = () => {
  if (!props.shouldStayOpen) {
    isOpen.value = false;

    if (props.isAlwaysOpen) {
      props.onClose?.();
    }
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  const actions = Array.from(
    dropdownBody?.value?.querySelectorAll<HTMLElement>('.dropdown-action') || [],
  );

  if (!actions) return;

  const focussedIndex = actions.findIndex((action) => action === document.activeElement);

  switch (event.key) {
    case 'ArrowDown': {
      const isLast = focussedIndex === actions.length - 1;
      actions.at(isLast ? 0 : focussedIndex + 1)?.focus();

      break;
    }
    case 'ArrowUp': {
      actions.at(focussedIndex - 1)?.focus();

      break;
    }
    default:
      return;
  }
};

watch(isOpen, (value) => {
  props.onUpdate?.(value);

  if (value) {
    props.onOpen?.();
  } else {
    props.onClose?.();
  }
});

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
});
</script>

<template>
  <Dropdown
    ref="dropdown"
    class="dropdown"
    :shown="isAlwaysOpen || isOpen"
    :triggers="triggers"
    :showTriggers="showTriggers"
    :hideTriggers="hideTriggers"
    :distance="distance"
    :placement="placement"
    :autoHide="!isPersistent"
    eagerMount
    autoBoundaryMaxSize
    :ariaExpanded="isOpen ? 'true' : 'false'"
    :onUpdate:shown="handleUpdateVisibility"
  >
    <slot
      v-if="!isAlwaysOpen"
      name="trigger"
      :class="['dropdown-trigger', isOpen && 'is-open']"
      :isOpen="isOpen"
    />

    <span v-else />

    <template #popper>
      <div class="dropdown-backdrop" />

      <div ref="dropdownBody" :onClick="handleClickMenu" :style="{ maxWidth }">
        <slot v-if="!destroyOnClose || isAlwaysOpen || isOpen" name="content" />
      </div>
    </template>
  </Dropdown>
</template>

<style lang="scss">
@use './Dropdown.scss';
</style>
